import { addonOptionDisplayLabel } from './bookingAssetAddons';
import type { AppLanguage } from '../i18n/types';
import { formatBookingListDate } from './bookingListDisplay';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function labelizeKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export const BOOKING_DETAIL_DATE_KEYS = new Set([
  'start_date',
  'end_date',
  'booking_date',
  'created_at',
  'updated_at',
  'cancelled_at',
]);

/** Omitted from the Details grid — rendered elsewhere or not user-facing. */
export const BOOKING_DETAIL_HIDDEN_KEYS = new Set(['status_id', 'addons', 'asset_name']);

export function bookingDetailFieldLabel(key: string): string {
  if (key === 'status_name') {
    return 'Status';
  }
  return labelizeKey(key);
}

function formatGroupedNumber(n: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 20 }).format(n);
}

function formatPriceValue(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return formatGroupedNumber(value);
  }
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value.trim());
    if (Number.isFinite(n)) {
      return formatGroupedNumber(n);
    }
    return value.trim();
  }
  return null;
}

export function formatBookingDetailFieldValue(
  key: string,
  value: unknown,
  language?: AppLanguage,
): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (BOOKING_DETAIL_DATE_KEYS.has(key)) {
    const formatted = formatBookingListDate(value, language);
    if (formatted) {
      return formatted;
    }
  }
  if (key === 'total_price') {
    const price = formatPriceValue(value);
    if (price) {
      return price;
    }
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '—';
  }
  if (typeof value === 'string') {
    return value.trim() || '—';
  }
  return JSON.stringify(value);
}

export type BookingAddonDisplayRow = {
  key: string;
  label: string;
  quantity: string;
  price: string | null;
};

function addonRowId(row: Record<string, unknown>): number | null {
  const raw = row.addon_id ?? row.id;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function addonRowQuantity(row: Record<string, unknown>): string {
  const raw = row.quantity ?? row.qty;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return String(Math.floor(raw));
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    if (Number.isFinite(n)) {
      return String(Math.floor(n));
    }
  }
  return '1';
}

/** Human-readable add-on rows for booking detail (empty array → no section). */
export function bookingAddonDisplayRows(
  addons: unknown,
  language: AppLanguage = 'en',
): BookingAddonDisplayRow[] {
  if (!Array.isArray(addons) || addons.length === 0) {
    return [];
  }
  const out: BookingAddonDisplayRow[] = [];
  for (let i = 0; i < addons.length; i++) {
    const item = addons[i];
    if (!isPlainObject(item)) {
      continue;
    }
    const addonId = addonRowId(item);
    const label = addonOptionDisplayLabel(item, undefined, addonId ?? i + 1, language);
    const price =
      formatPriceValue(item.addon_price ?? item.price ?? item.unit_price ?? item.total_price) ?? null;
    out.push({
      key: `${addonId ?? 'row'}-${i}`,
      label,
      quantity: addonRowQuantity(item),
      price,
    });
  }
  return out;
}

export function shouldSkipBookingDetailField(
  key: string,
  value: unknown,
  data: Record<string, unknown>,
): boolean {
  if (BOOKING_DETAIL_HIDDEN_KEYS.has(key)) {
    return true;
  }
  if (
    (key === 'start_date' || key === 'end_date') &&
    typeof data.start_date === 'string' &&
    data.start_date.trim() &&
    typeof data.end_date === 'string' &&
    data.end_date.trim()
  ) {
    return true;
  }
  if (key === 'status' && typeof data.status_name === 'string' && data.status_name.trim()) {
    return true;
  }
  if (
    (key === 'id' || key === 'booking_id') &&
    typeof value === 'string' &&
    value.trim() &&
    (value.trim() === String(data.id ?? '').trim() ||
      value.trim() === String(data.booking_id ?? '').trim())
  ) {
    return true;
  }
  return false;
}
